import { useState } from 'react';
import Badge from './Badge';
import { Modal } from './Modal';
import type { ChartingStatus } from '@/types';

interface StatusPillProps {
  status: ChartingStatus;
  onStatusChange?: (newStatus: ChartingStatus, comment?: string) => Promise<void>;
  disabled?: boolean;
  className?: string;
  requestNumber?: string;
  lineItemName?: string;
}

const STATUS_OPTIONS: { value: ChartingStatus; label: string; color: string }[] = [
  { value: 'open', label: 'Open', color: 'bg-[#EEE9F9] text-[#6B4FBB]' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-[#FEF3C7] text-[#92400E]' },
  { value: 'completed', label: 'Completed', color: 'bg-[#E8F5ED] text-[#1A7A3C]' },
  { value: 'approval_required_by_exception', label: 'Exception Required', color: 'bg-[#EEE9F9] text-[#6B4FBB]' },
  { value: 'revision_required_by_sales', label: 'Revision Required', color: 'bg-[#FEE2E2] text-[#B91C1C]' },
];

export function StatusPill({
  status,
  onStatusChange,
  disabled = false,
  className = '',
  requestNumber,
  lineItemName,
}: StatusPillProps) {
  const [showModal, setShowModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<ChartingStatus>(status);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentStatusOption = STATUS_OPTIONS.find(opt => opt.value === status);
  const selectedStatusOption = STATUS_OPTIONS.find(opt => opt.value === selectedStatus);
  const isClickable = !disabled && onStatusChange;

  const handleClick = () => {
    if (isClickable) {
      setSelectedStatus(status);
      setComment('');
      setError(null);
      setShowModal(true);
    }
  };

  const handleSubmit = async () => {
    if (!onStatusChange || selectedStatus === status) {
      setShowModal(false);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onStatusChange(selectedStatus, comment.trim() || undefined);
      setShowModal(false);
      setComment('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedStatus(status);
    setComment('');
    setError(null);
  };

  return (
    <>
      <Badge
        className={`${currentStatusOption?.color || 'bg-neutral-100 text-neutral-600'} ${
          isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
        } ${className}`}
        onClick={handleClick}
        title={isClickable ? 'Click to change status' : undefined}
      >
        {currentStatusOption?.label || status.replace(/_/g, ' ').toUpperCase()}
      </Badge>

      {/* Status Change Modal */}
      <Modal isOpen={showModal} onClose={handleCancel} size="lg">
          <div className="bg-white rounded-lg p-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">Update Status</h2>

            {/* Request and Line Item Info */}
            {(requestNumber || lineItemName) && (
              <div className="mb-6 space-y-2">
                {requestNumber && (
                  <p className="text-base text-neutral-600">
                    <span className="font-medium">Request:</span> {requestNumber}
                  </p>
                )}
                {lineItemName && (
                  <p className="text-base text-neutral-600">
                    <span className="font-medium">Line Item:</span> {lineItemName}
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-status-negative-bg border border-red-200 rounded-md">
                <p className="text-sm text-status-negative font-medium">{error}</p>
              </div>
            )}

            {/* Current Status */}
            <div className="mb-6">
              <label className="block text-base font-semibold text-neutral-600 mb-3">
                Current Status
              </label>
              <Badge className={currentStatusOption?.color || 'bg-neutral-100 text-neutral-600'}>
                {currentStatusOption?.label || status}
              </Badge>
            </div>

            {/* New Status Selection */}
            <div className="mb-6">
              <label className="block text-base font-semibold text-neutral-600 mb-3">
                New Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as ChartingStatus)}
                className="w-full px-4 py-3 text-base border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
                disabled={isSubmitting}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {/* Preview badge with min-height to prevent layout shift */}
              <div className="mt-3 min-h-[32px]">
                {selectedStatus !== status && selectedStatusOption && (
                  <Badge className={selectedStatusOption.color}>
                    {selectedStatusOption.label}
                  </Badge>
                )}
              </div>
            </div>

            {/* Comments */}
            <div className="mb-8">
              <label className="block text-base font-semibold text-neutral-600 mb-3">
                Comments (optional)
              </label>
              <textarea
                className="w-full px-4 py-3 text-base border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent resize-none"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add any notes about this status change..."
                disabled={isSubmitting}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-6 py-3 text-base font-semibold text-white bg-outfront-charcoal rounded-lg hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-outfront-charcoal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-3 text-base font-semibold text-white bg-outfront-purple rounded-lg hover:bg-outfront-purple-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-outfront-purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || selectedStatus === status}
              >
                {isSubmitting ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
      </Modal>
    </>
  );
}
